import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CalendarDays,
  Clock,
  ClipboardList,
  CreditCard,
  GraduationCap,
  X,
  type LucideIcon,
  ArrowDown,
} from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { TenantOrganizationSiteHomepage } from "@/components/organization/tenant-organization-site-homepage";
import { Footer } from "@/components/footer";
import { useIsTenantOrganizationSiteHost } from "@/lib/organization-site-host";
import { publicHomepageStructuredDataJson } from "@/lib/public-homepage-structured-data";

export const Route = createFileRoute("/_marketing/")({
  head: () => ({
    scripts: [{ type: "application/ld+json", children: publicHomepageStructuredDataJson }],
  }),
  component: MarketingHomePage,
});

function MarketingHomePage() {
  const isTenantOrganizationSite = useIsTenantOrganizationSiteHost();

  if (isTenantOrganizationSite) {
    return <TenantOrganizationSiteHomepage />;
  }

  return <App />;
}

interface Benefit {
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  description: string;
  preview: ReactNode;
}

// Mini preview UIs styled to mirror the actual product surfaces
// (Applications table, Schedule list, Payments breakdown).

const enrollmentPreview = (
  <div className="overflow-hidden rounded-md border border-border bg-background">
    <div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Algebra I · Roster
      </span>
      <span className="text-[10px] text-muted-foreground">5 students</span>
    </div>
    {[
      { name: "Ada Hawthorn", status: "Enrolled", tone: "ok" },
      { name: "Mateo Reyes", status: "Enrolled", tone: "ok" },
      { name: "Chidi Okafor", status: "Waitlisted", tone: "warn" },
      { name: "Iris Bell", status: "Enrolled", tone: "ok" },
      { name: "Jun Tanaka", status: "Waitlisted", tone: "warn" },
    ].map((row, i, arr) => (
      <div
        key={row.name}
        className={
          "flex items-center justify-between gap-3 px-3 py-2 text-xs " +
          (i < arr.length - 1 ? "border-b border-border" : "")
        }
      >
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[9px] font-semibold text-primary dark:text-primary-foreground">
            {row.name
              .split(" ")
              .map((p) => p[0])
              .join("")}
          </span>
          <span className="truncate font-medium text-foreground">{row.name}</span>
        </div>
        <span
          className={
            "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium " +
            (row.tone === "ok"
              ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/20 dark:text-emerald-300"
              : "bg-amber-100 text-amber-900 dark:bg-amber-500/20 dark:text-amber-200")
          }
        >
          {row.status}
        </span>
      </div>
    ))}
  </div>
);

const operationsPreview = (
  <div className="flex flex-col gap-y-2">
    <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      <span>Wednesday</span>
      <span>3 classes</span>
    </div>
    {[
      { time: "9:00 AM", className: "Algebra I", teacher: "J. Whitford", color: "bg-primary/15 border-primary/30" },
      { time: "10:30 AM", className: "Studio Art", teacher: "K. Reyes", color: "bg-accent/15 border-accent/30" },
      { time: "1:00 PM", className: "World Lit", teacher: "D. Okafor", color: "bg-primary/15 border-primary/30" },
    ].map((row) => (
      <div
        key={row.className}
        className={"flex items-center gap-3 rounded-md border bg-background px-3 py-2 text-xs " + row.color}
      >
        <div className="flex w-12 flex-col leading-tight">
          <span className="text-[10px] font-semibold text-muted-foreground">{row.time}</span>
        </div>
        <div className="flex flex-1 flex-col leading-tight">
          <span className="font-semibold text-foreground">{row.className}</span>
          <span className="text-[10px] text-muted-foreground">{row.teacher}</span>
        </div>
      </div>
    ))}
  </div>
);

const tuitionPreview = (
  <div className="flex flex-col gap-y-3">
    <div className="grid grid-cols-3 gap-2">
      {[
        { label: "Total Due", value: "$1,420", tone: "text-foreground" },
        { label: "Paid", value: "$1,200", tone: "text-emerald-800 dark:text-emerald-300" },
        { label: "Balance", value: "$220", tone: "text-primary dark:text-primary-foreground" },
      ].map((s) => (
        <div key={s.label} className="rounded-md border border-border bg-background px-2 py-1.5">
          <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</p>
          <p className={"text-sm font-bold " + s.tone}>{s.value}</p>
        </div>
      ))}
    </div>
    <div className="flex flex-col gap-y-1.5">
      {[
        { name: "Algebra I", price: "$320" },
        { name: "Studio Art", price: "$280" },
        { name: "World Lit", price: "$320" },
      ].map((c) => (
        <div
          key={c.name}
          className="flex items-center justify-between gap-2 rounded-md bg-background px-3 py-1.5 text-xs border border-border"
        >
          <div className="flex items-center gap-2 truncate">
            <GraduationCap className="size-3 shrink-0 text-muted-foreground" />
            <span className="truncate font-medium text-foreground">{c.name}</span>
          </div>
          <span className="shrink-0 text-muted-foreground">{c.price}</span>
        </div>
      ))}
    </div>
  </div>
);

const benefits: Benefit[] = [
  {
    icon: ClipboardList,
    eyebrow: "Enrollment",
    title: "Applications, registration, and waitlists in one flow",
    description:
      "Stop stitching together forms, spreadsheets, and email. Families apply once and you see exactly where every student stands.",
    preview: enrollmentPreview,
  },
  {
    icon: CalendarDays,
    eyebrow: "Schedules",
    title: "A class schedule everyone shares",
    description:
      "Class days, classes, and student schedules live in one place — every parent sees the same up-to-date view you do.",
    preview: operationsPreview,
  },
  {
    icon: CreditCard,
    eyebrow: "Tuition",
    title: "Tuition and fees in one place",
    description:
      "Set fee structures once. Track tuition, fees, and recorded payments per family without rebuilding a spreadsheet every term.",
    preview: tuitionPreview,
  },
];

const problemSignals: { label: string; icon: LucideIcon }[] = [
  { label: "Duplicate family details across multiple tools", icon: ClipboardList },
  { label: "Schedule changes buried in email threads", icon: CalendarDays },
  { label: "Manual tuition cleanup at month end", icon: CreditCard },
  { label: "Answering the same questions every week", icon: Clock },
];

function App() {
  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex-1">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary text-primary-foreground">
          {/* Abstract background A: layered radial + conic gradients in earthy primary/accent tones */}
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              backgroundImage: [
                "radial-gradient(60% 50% at 12% 18%, hsl(var(--accent) / 0.30), transparent 70%)",
                "radial-gradient(50% 60% at 88% 10%, hsl(var(--primary-foreground) / 0.18), transparent 65%)",
                "radial-gradient(70% 70% at 100% 100%, hsl(var(--accent) / 0.18), transparent 60%)",
                "conic-gradient(from 220deg at 30% 80%, hsl(var(--primary)) 0deg, hsl(var(--primary) / 0.6) 90deg, hsl(var(--primary)) 200deg)",
              ].join(", "),
            }}
          />
          {/* Subtle grid overlay for texture */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "linear-gradient(hsl(var(--primary-foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary-foreground)) 1px, transparent 1px)",
              backgroundSize: "56px 56px",
            }}
          />
          {/* Smoother transition into next section — starts well below the CTAs */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-48"
            style={{
              backgroundImage:
                "linear-gradient(to bottom, transparent 0%, transparent 55%, hsl(var(--background) / 0.6) 80%, hsl(var(--background)) 100%)",
            }}
          />

          <div className="container relative grid gap-12 pb-32 pt-20 md:pb-40 md:pt-28 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-10">
            <div className="order-2 lg:order-1">
              <h1 className="max-w-2xl text-balance text-4xl font-semibold leading-[1.04] tracking-[-0.035em] md:text-6xl lg:text-[56px]">
                Simpler operations for flexible learning communities.
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-primary-foreground/85 md:text-xl">
                Manage enrollment, schedules, family records, and tuition in one place, so you and your families always
                know what comes next.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-accent px-7 font-semibold text-accent-foreground shadow-lg hover:bg-terracotta-dark"
                >
                  <Link to="/contact-us">
                    Contact us
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-primary-foreground/30 bg-transparent px-6 font-medium text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                >
                  <a href="#how-it-works">
                    See how it works
                    <ArrowDown className="ml-2 size-4" />
                  </a>
                </Button>
              </div>
            </div>

            {/* Real product screenshot — angled, light + dark variants, no fake browser chrome */}
            <div className="relative order-1 lg:order-2" style={{ perspective: "1600px" }}>
              {/* Soft glow */}
              <div aria-hidden className="absolute -inset-10 rounded-[2rem] bg-accent/20 blur-3xl" />
              {/* Floating accent badge */}
              <div className="absolute -left-4 top-8 z-20 hidden rotate-[-6deg] items-center gap-2 rounded-md border border-border bg-background/95 px-3 py-2 text-[11px] font-semibold text-foreground shadow-xl backdrop-blur md:flex">
                <span className="flex size-1.5 rounded-full bg-emerald-500" />
                12 applications in review
              </div>
              {/* Floating accent badge 2 */}
              <div className="absolute -right-3 bottom-10 z-20 hidden rotate-[5deg] items-center gap-2 rounded-md border border-border bg-background/95 px-3 py-2 text-[11px] font-semibold text-foreground shadow-xl backdrop-blur md:flex">
                <CalendarDays className="size-3 text-accent" />
                Wednesday · 3 classes
              </div>
              <div
                className="relative overflow-hidden rounded-2xl bg-background shadow-[0_30px_80px_-20px_rgba(0,0,0,0.55)] ring-1 ring-black/10 dark:ring-white/10"
                style={{
                  transform: "rotateY(-12deg) rotateX(6deg) rotate(0.4deg)",
                  transformStyle: "preserve-3d",
                }}
              >
                {/* Thin accent bar replaces the fake browser chrome */}
                <div className="h-1.5 w-full bg-gradient-to-r from-accent via-primary to-accent" />
                {/* Light mode screenshot */}
                <picture className="block dark:hidden">
                  <source srcSet="/hero-screenshot-light.webp" type="image/webp" />
                  <img
                    src="/hero-screenshot-light.png"
                    alt="Veronics admin Families dashboard showing a list of families with adults and children counts"
                    width="1080"
                    height="615"
                    className="h-auto w-full"
                    fetchPriority="high"
                    loading="eager"
                  />
                </picture>
                {/* Dark mode screenshot */}
                <picture className="hidden dark:block">
                  <source srcSet="/hero-screenshot-dark.webp" type="image/webp" />
                  <img
                    src="/hero-screenshot-dark.png"
                    alt="Veronics admin Families dashboard showing a list of families with adults and children counts"
                    width="1080"
                    height="615"
                    className="h-auto w-full"
                    loading="eager"
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEM */}
        <section className="bg-background py-16 md:py-20">
          <div className="container">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-primary/80 dark:text-primary-foreground/80">
                  The problem
                </p>
                <h2 className="mt-3 text-3xl font-semibold leading-tight text-primary dark:text-foreground md:text-4xl">
                  Patchwork tools create more work as your program grows
                </h2>
              </div>
              <div>
                <p className="text-lg leading-8 text-muted-foreground">
                  Forms, spreadsheets, and email threads can work for a while. As enrollment grows, they create more
                  cleanup for you, more confusion for parents, and more chances for important details to fall through
                  the cracks.
                </p>
                <ul className="mt-7 grid gap-x-6 gap-y-3 sm:grid-cols-2">
                  {problemSignals.map(({ label, icon: Icon }) => (
                    <li key={label} className="group flex items-center gap-3 text-sm font-medium text-foreground">
                      <span className="relative flex size-9 shrink-0 items-center justify-center rounded-md bg-accent/10 text-accent">
                        <Icon className="size-4" aria-hidden />
                        <span className="absolute -right-1 -top-1 flex size-3.5 items-center justify-center rounded-full bg-accent text-accent-foreground">
                          <X className="size-2.5" aria-hidden />
                        </span>
                      </span>
                      <span>{label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* BENEFITS — light background with mini product previews */}
        <section id="how-it-works" className="bg-muted/40 py-16 dark:bg-card/40 md:py-24">
          <div className="container">
            <div className="mx-auto mb-12 max-w-2xl text-center">
              <p className="text-sm font-semibold uppercase tracking-wide text-primary/80 dark:text-primary-foreground/80">
                How Veronics works
              </p>
              <h2 className="mt-3 text-3xl font-semibold leading-tight text-primary dark:text-foreground md:text-4xl">
                The workflows that keep your week moving
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div
                    key={benefit.title}
                    className="flex flex-col rounded-xl border border-border bg-card p-6 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <div className="mb-4 flex items-center gap-3">
                      <div className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary dark:text-primary-foreground">
                        <IconComponent className="size-5" />
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        0{index + 1} · {benefit.eyebrow}
                      </p>
                    </div>
                    <h3 className="text-xl font-semibold leading-snug text-foreground">{benefit.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">{benefit.description}</p>
                    <div className="mt-6 rounded-lg border border-border bg-muted/40 p-3">{benefit.preview}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 text-center">
              <Link
                to="/features"
                className="font-medium text-primary underline-offset-4 hover:underline dark:text-foreground"
              >
                Explore all features →
              </Link>
            </div>
          </div>
        </section>

        {/* WHY VERONICS — single section, replaces both old "Why" + "Proof" slabs */}
        <section className="bg-background py-16 md:py-24">
          <div className="container">
            <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-primary/80 dark:text-primary-foreground/80">
                  Why Veronics
                </p>
                <h2 className="mt-3 text-3xl font-semibold leading-tight text-primary dark:text-foreground md:text-4xl">
                  Designed for the way you run your program
                </h2>
                <p className="mt-5 text-lg leading-8 text-muted-foreground">
                  Veronics supports homeschool co-ops, microschools, hybrid programs, and enrichment groups that have
                  outgrown patchwork processes but don&rsquo;t need software built for district-sized institutions. Get
                  more structure and better visibility without adopting a district model.
                </p>
              </div>

              <div className="rounded-xl border border-border bg-card p-8 shadow-sm md:p-10">
                <p className="text-sm font-semibold uppercase tracking-wide text-primary/80 dark:text-primary-foreground/80">
                  In live use
                </p>
                <div className="mt-6 grid gap-6 sm:grid-cols-3">
                  <div>
                    <p className="text-4xl font-bold text-primary dark:text-foreground">100+</p>
                    <p className="mt-1 text-sm text-muted-foreground">families supported in production today</p>
                  </div>
                  <div>
                    <p className="text-4xl font-bold text-primary dark:text-foreground">2025</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      first homeschool co-op live on Veronics, end-to-end
                    </p>
                  </div>
                  <div>
                    <p className="text-4xl font-bold text-accent">1</p>
                    <p className="mt-1 text-sm text-muted-foreground">place to run your program</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA — the only dark slab on the page so it actually punches */}
        <section className="relative overflow-hidden bg-primary py-20 text-primary-foreground md:py-24">
          {/* Abstract background B: dot grid + diagonal terracotta wash, distinct from hero */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.18]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, hsl(var(--primary-foreground)) 1px, transparent 1.5px)",
              backgroundSize: "22px 22px",
            }}
          />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(115deg, hsl(var(--accent) / 0.18) 0%, transparent 45%, transparent 60%, hsl(var(--primary-foreground) / 0.10) 100%)",
            }}
          />
          <div className="container relative">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-semibold leading-tight md:text-5xl">
                Spend less time managing tools, more time building your community
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-primary-foreground/80">
                Keep the details organized, families informed, and every term moving forward with Veronics.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-accent px-7 font-semibold text-accent-foreground shadow-lg hover:bg-terracotta-dark"
                >
                  <Link to="/contact-us">
                    Contact us
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
